import React, { useState, useCallback } from 'react';
import { View, LayoutChangeEvent } from 'react-native';
import { Svg, Path } from 'react-native-svg';

import { ModalArrowPointerProps } from '@v2/pages/planboard/types';
import { MODAL_ARROW } from '@v2/pages/planboard/constants';

// Shadow style for SVG - uses filter for path-following shadow
const svgShadowStyle = {
  filter: 'drop-shadow(0px 2px 8px rgba(0, 0, 0, 0.15))',
};

const { TRIANGLE_SIZE_CONFIG, TRIANGLE_CONTENT_OFFSET, ARROW_COLOR } = MODAL_ARROW;

const buildTopTrianglePath = (
  width: number,
  height: number,
  triangleCenter: number,
  borderRadius: number
): string => {
  const triangleWidth = 30;
  const hideTriangleLeftCorner = triangleCenter - triangleWidth / 2 < 5;
  const hideTriangleRightCorner = triangleCenter + triangleWidth / 2 > width - 5;

  return `
    M${triangleCenter + TRIANGLE_SIZE_CONFIG} ${TRIANGLE_SIZE_CONFIG}
    L${triangleCenter} 0
    L${triangleCenter - TRIANGLE_SIZE_CONFIG} ${TRIANGLE_SIZE_CONFIG}
    C${triangleCenter - 8.736} 8.73572 ${triangleCenter - 11.788} ${TRIANGLE_CONTENT_OFFSET} ${triangleCenter - 14.971} ${TRIANGLE_CONTENT_OFFSET}
    L${triangleCenter - 20} ${TRIANGLE_CONTENT_OFFSET}
    L${hideTriangleLeftCorner ? 0 : borderRadius} ${TRIANGLE_CONTENT_OFFSET}
    ${hideTriangleLeftCorner ? '' : `A${borderRadius} ${borderRadius} 0 0 0 0 ${TRIANGLE_CONTENT_OFFSET + borderRadius}`}
    V${height - borderRadius}
    A${borderRadius} ${borderRadius} 0 0 0 ${borderRadius} ${height}
    H${width - borderRadius}
    A${borderRadius} ${borderRadius} 0 0 0 ${width} ${height - borderRadius}
    V${TRIANGLE_CONTENT_OFFSET + borderRadius}
    ${hideTriangleRightCorner ? '' : `A${borderRadius} ${borderRadius} 0 0 0 ${width - borderRadius} ${TRIANGLE_CONTENT_OFFSET}`}
    H${triangleCenter + 20}
    L${triangleCenter + 14.971} ${TRIANGLE_CONTENT_OFFSET}
    C${triangleCenter + 11.788} ${TRIANGLE_CONTENT_OFFSET} ${triangleCenter + 8.736} 8.73571 ${triangleCenter + 6.485} 6.48528
    Z
  `;
};

const buildBottomTrianglePath = (
  width: number,
  height: number,
  triangleCenter: number,
  borderRadius: number
): string => {
  const triangleConfig = height - TRIANGLE_SIZE_CONFIG;
  const triangleOffset = height - TRIANGLE_CONTENT_OFFSET;
  const triangleWidth = 30;

  const hideTriangleLeftCorner = triangleCenter - triangleWidth / 2 < 5;
  const hideTriangleRightCorner = triangleCenter + triangleWidth / 2 > width - 5;

  return `
    M${triangleCenter + TRIANGLE_SIZE_CONFIG} ${triangleConfig}
    L${triangleCenter} ${height}
    L${triangleCenter - TRIANGLE_SIZE_CONFIG} ${triangleConfig}
    C${triangleCenter - 8.736} ${height - 8.73572} ${triangleCenter - 11.788} ${triangleOffset} ${triangleCenter - 14.971} ${triangleOffset}
    L${triangleCenter - 20} ${triangleOffset}
    L${hideTriangleLeftCorner ? 0 : borderRadius} ${triangleOffset}
    ${hideTriangleLeftCorner ? '' : `A${borderRadius} ${borderRadius} 0 0 1 0 ${triangleOffset - borderRadius}`}
    V${borderRadius}
    A${borderRadius} ${borderRadius} 0 0 1 ${borderRadius} 0
    H${width - borderRadius}
    A${borderRadius} ${borderRadius} 0 0 1 ${width} ${borderRadius}
    V${triangleOffset - borderRadius}
    ${hideTriangleRightCorner ? '' : `A${borderRadius} ${borderRadius} 0 0 1 ${width - borderRadius} ${triangleOffset}`}
    H${triangleCenter + 20}
    L${triangleCenter + 14.971} ${triangleOffset}
    C${triangleCenter + 11.788} ${triangleOffset} ${triangleCenter + 8.736} ${triangleOffset + 1.26429} ${triangleCenter + 6.485} ${triangleOffset + 3.51472}
    Z
  `;
};

const ModalArrowPointer: React.FC<ModalArrowPointerProps> = ({
  position,
  scale,
  C,
  width,
  maxHeight,
  borderRadius: borderRadiusProp,
  children,
  contentContainerStyle,
}) => {
  const [contentHeight, setContentHeight] = useState(0);
  const borderRadius = scale(borderRadiusProp ?? 33);
  const arrowHeight = scale(MODAL_ARROW.HEIGHT);
  const padding = scale(8); // Small padding between arrow tip and anchor

  // Measure content height, clamped to maxHeight
  const handleContentLayout = useCallback(
    (event: LayoutChangeEvent) => {
      const { height: measuredHeight } = event.nativeEvent.layout;
      setContentHeight(Math.min(measuredHeight, maxHeight));
    },
    [maxHeight]
  );

  // Use measured height or 0 if not yet measured
  const effectiveHeight = contentHeight || 0;
  const totalHeight = effectiveHeight + arrowHeight;
  const arrowCenter = position.arrowHorizontalPosition;
  const needsScroll = contentHeight >= maxHeight;

  // Arrow points down when modal opens above, points up when modal opens below
  const path =
    effectiveHeight > 0
      ? position.isOpeningAbove
        ? buildBottomTrianglePath(width, totalHeight, arrowCenter, borderRadius)
        : buildTopTrianglePath(width, totalHeight, arrowCenter, borderRadius)
      : '';

  // Content positioning - offset by triangle height when triangle is at top
  const contentTop = position.isOpeningAbove ? 0 : arrowHeight;

  // Modal positioning based on actual measured height
  let modalTop: number;
  if (position.isOpeningAbove) {
    // Opening above: position modal so arrow tip points to anchor
    // anchorTop is where modal bottom should align, subtract total height
    modalTop = position.anchorTop - totalHeight + padding;
  } else {
    // Opening below: arrow is at top, position so arrow tip is near anchor
    modalTop = position.top - arrowHeight;
  }

  return (
    <View
      style={{
        position: 'absolute',
        top: modalTop,
        left: position.left,
        width,
      }}
    >
      {/* SVG Background with shadow - only render when we have measured height */}
      {effectiveHeight > 0 && (
        <Svg width={width} height={totalHeight} style={svgShadowStyle}>
          <Path d={path} fill={C(ARROW_COLOR)} />
        </Svg>
      )}

      {/* Content container - measures its own height */}
      <View
        style={[
          {
            position: effectiveHeight > 0 ? 'absolute' : 'relative',
            top: effectiveHeight > 0 ? contentTop : 0,
            left: 0,
            right: 0,
            maxHeight,
            borderRadius,
            overflow: needsScroll ? 'scroll' : 'hidden',
            // Hide during measurement to prevent flash
            opacity: effectiveHeight > 0 ? 1 : 0,
          },
          contentContainerStyle,
        ]}
        onLayout={handleContentLayout}
      >
        {children}
      </View>
    </View>
  );
};

export default ModalArrowPointer;
